import { Box, Text } from '@chakra-ui/react';
import { Col, Form, Row, Select, TreeSelect } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';

import {
  SellYourFormValues,
  TransformedBrand,
  TransformedModel,
} from '@/app/sell-my-car/service';
import { AVAILABLE_CAR_YEARS } from '@/constants/sell-your-car/sell-your-car.constants';
import { Province } from '@/services/province/province.service';

import {
  processModelSelection,
  processVehicleMakeSelection,
} from '../../_helpers/vehicleSelectionHelpers';
import { fetchModelsByRangeId } from '../../actions';
import Styles from '../SellYourCarForm.module.scss';

interface VehicleSelectionStepProps {
  control: Control<SellYourFormValues>;
  errors: FieldErrors<SellYourFormValues>;
  treeData: TransformedBrand[];
  setValue: UseFormSetValue<SellYourFormValues>;
  provincesData: Province[];
  getValues: UseFormGetValues<SellYourFormValues>;
}

const ProvinceSelector = ({
  control,
  errors,
  provincesData = [],
}: {
  control: Control<SellYourFormValues>;
  errors: FieldErrors<SellYourFormValues>;
  provincesData: Province[];
}) => (
  <Col sm={12} style={{ width: '100%' }}>
    <Controller
      name="province"
      control={control}
      rules={{ required: 'Please select a province' }}
      render={({ field }) => (
        <Form.Item label="" className={Styles.formLabel} layout="vertical">
          <Select
            {...field}
            className={Styles.formInput}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            placeholder="Select province"
            options={provincesData.map((province) => ({
              label: province.name,
              value: province.id.toString(),
              key: province.id,
            }))}
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
          />
        </Form.Item>
      )}
    />
    {errors.province && (
      <Text color="red.500" fontSize="sm">
        {errors.province?.message?.toString() || 'Please select a province'}
      </Text>
    )}
  </Col>
);

const YearSelector = ({
  control,
  errors,
}: {
  control: Control<SellYourFormValues>;
  errors: FieldErrors<SellYourFormValues>;
}) => (
  <Col sm={12} style={{ width: '100%' }}>
    <Controller
      name="vehicle_year"
      control={control}
      rules={{ required: 'Please select year of manufacture' }}
      render={({ field }) => (
        <Form.Item label="" className={Styles.formLabel} layout="vertical">
          <TreeSelect
            {...field}
            className={Styles.formInput}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            placeholder="Select year"
            treeData={AVAILABLE_CAR_YEARS.map((year) => ({
              title: year,
              value: year,
              key: year,
            }))}
            showSearch
            allowClear
          />
        </Form.Item>
      )}
    />
    {errors.vehicle_year && (
      <Text color="red.500" fontSize="sm">
        {errors.vehicle_year?.message?.toString() || 'Please select a year'}
      </Text>
    )}
  </Col>
);

const MakeRangeSelector = ({
  control,
  errors,
  treeData,
  onMakeRangeChange,
}: {
  control: Control<SellYourFormValues>;
  errors: FieldErrors<SellYourFormValues>;
  treeData: TransformedBrand[];
  onMakeRangeChange: (value: any) => void;
}) => (
  <Col sm={12} style={{ width: '100%' }}>
    <Controller
      name="custom_vehicle_brand"
      control={control}
      rules={{ required: 'Please select brand & model' }}
      render={({ field }) => (
        <Form.Item label="" layout="vertical" className={Styles.formLabel}>
          <TreeSelect
            {...field}
            className={Styles.formInput}
            style={{ width: '100%' }}
            dropdownStyle={{
              maxHeight: 400,
              overflow: 'auto',
              padding: '8px',
            }}
            treeData={treeData}
            placeholder="Select Make & Range"
            showSearch
            allowClear
            treeDefaultExpandAll={false}
            filterTreeNode={(input, node) => {
              if (!input || !input.trim()) return true;

              const searchTerm = input.replaceAll(' ', '').toLowerCase();
              const nodeTitle = ((node?.customTitle || node?.title) as string)
                ?.toLowerCase()
                .split(' | ')
                .map((val) => val.replaceAll(' ', ''))
                .join('');

              return nodeTitle?.indexOf(searchTerm) >= 0;
            }}
            onChange={(value) => {
              field.onChange(value);
              onMakeRangeChange(value);
            }}
            labelInValue={true}
            treeNodeLabelProp="customTitle"
          />
        </Form.Item>
      )}
    />
    {errors.custom_vehicle_brand && (
      <Text color="red.500" fontSize="sm">
        {errors.custom_vehicle_brand.message?.toString() ||
          'Please select brand & model'}
      </Text>
    )}
  </Col>
);

const ModelSelector = ({
  control,
  errors,
  models,
  isLoadingModels,
  isDisabled,
  onModelChange,
}: {
  control: Control<SellYourFormValues>;
  errors: FieldErrors<SellYourFormValues>;
  models: TransformedModel[];
  isLoadingModels: boolean;
  isDisabled: boolean;
  onModelChange: (value: string, option: any) => void;
}) => (
  <Col sm={12} style={{ width: '100%' }}>
    <Controller
      name="vehicle_model"
      control={control}
      render={({ field }) => (
        <Form.Item label="" layout="vertical" className={Styles.formLabel}>
          <Select
            {...field}
            className={Styles.formInput}
            style={{ width: '100%' }}
            placeholder={
              isLoadingModels
                ? 'Loading models...'
                : 'Select specific model (optional)'
            }
            showSearch
            allowClear
            disabled={isDisabled || isLoadingModels}
            loading={isLoadingModels}
            options={isLoadingModels ? [] : (models || []).map((model) => ({
              label: model.title,
              value: model.value,
              key: model.key,
              fuelType: model['fuel-type'], 
              transmission: model.transmission,
            }))}
            filterOption={(input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            onChange={(value, option) => {
              field.onChange(value);
              onModelChange(value, option);
            }}
          />
        </Form.Item>
      )}
    />
    {errors.vehicle_model && (
      <Text color="red.500" fontSize="sm">
        {errors.vehicle_model.message?.toString()}
      </Text>
    )}
  </Col>
);

const VehicleSelectionStep: React.FC<VehicleSelectionStepProps> = ({
  control,
  errors,
  treeData,
  setValue,
  provincesData = [],
  getValues,
}) => {
  const [models, setModels] = useState<TransformedModel[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  useEffect(() => {
    async function fetchingData() {
      if (getValues('custom_vehicle_brand') && !models.length) {
        setIsLoadingModels(true);
        const value = getValues('custom_vehicle_brand');
        const customBrandRange = value?.value.split('|');
        const rangeId =
          customBrandRange?.length > 2 ? customBrandRange[2] : null;

        if (rangeId) {
          const modelData = await fetchModelsByRangeId(Number(rangeId));

          setModels(modelData);
          setIsLoadingModels(false);
        }
      }
    }
    fetchingData();
  }, []);
  const handleMakeRangeSelection = async (value: {
    value: string;
    label?: string;
  }) => {
    if (!setValue) return;

    setIsLoadingModels(true);

    try {
      const { models: newModels } = await processVehicleMakeSelection(
        value,
        treeData,
        setValue,
      );

      setModels(newModels);
    } catch (error) {
      console.error('Error handling make/range selection:', error);
      setModels([]);
    } finally {
      setIsLoadingModels(false);
    }
  };

  const handleModelSelection = (value: string) => {
    processModelSelection(value, models, setValue);
  };

  return (
    <Box>
      <Row gutter={12}>
        <ProvinceSelector
          control={control}
          errors={errors}
          provincesData={provincesData}
        />

        <YearSelector control={control} errors={errors} />
      </Row>
      <Row gutter={12} style={{ marginTop: '12px' }}>
        <MakeRangeSelector
          control={control}
          errors={errors}
          treeData={treeData}
          onMakeRangeChange={handleMakeRangeSelection}
        />
        <ModelSelector
          control={control}
          errors={errors}
          models={models}
          isLoadingModels={isLoadingModels}
          isDisabled={!getValues('vehicle_range') || isLoadingModels}
          onModelChange={handleModelSelection}
        />
      </Row>
    </Box>
  );
};

export default VehicleSelectionStep;
